import { Select, DatePicker, Input, Col, Row, Form, Typography } from "antd";
import axios from "axios";
import React, { useEffect, useState, useContext } from "react";
import { leaveTypesArray } from "../../../commons/Arrays/LeavesTypesArray";
import { isAuthenticated } from "../../../commons/Auth/auth";
import MainButtonComp from "../../../commons/Buttons/MainButtonComp";
import { Error, Success } from "../../../commons/Messages/Messages";
import { SocketContext } from "../../../context/SocketContext";
import { sendLeave } from "../../../commons/Appointments/appointmentApi";
import GeneralModal from "../General.modal";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const { TextArea } = Input;
const { Title } = Typography;

const AddLeaves = ({ updateParent }) => {
  const { t } = useTranslation("dashboard");
  const socket = useContext(SocketContext);
  const [employeesArray, setEmployeesArray] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const showModal = () => {
    setIsModalOpen(true);
    form.resetFields();
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  const onFinish = async values => {
    setLoading(true);
    const payload = {
      ...values,
      serviceProvider: isAuthenticated()?.serviceProviderId,
      startDate: values.startDate ? values.startDate.format("DD/MMM/YYYY") : "",
      endDate: values.endDate ? values.endDate.format("DD/MMM/YYYY") : ""
    };

    try {
      const res = await sendLeave("post", "/add", payload, t);

      setLoading(false);
      if (!res) return; // kept "Cancel" in the appointments dialog
      if (res.status === 200 || res.status === 304) {
        Success(res.data.successMessage || "Leave added successfully!");
        if (updateParent) updateParent();
        handleCancel();
        socket.emit("refresh service provider data", {
          user: isAuthenticated()?.serviceProviderId
        });
      } else {
        Error(res.data.errorMessage);
      }
    } catch (err) {
      setLoading(false);
      generalErrorAlert(err);
    }
  };

  const getData = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/employees/employee/creator/${isAuthenticated()?.serviceProviderId}`,
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token")
          }
        }
      );
      if (res.status === 200 || res.status === 304) {
        const makeOptions = res.data?.map(d => ({
          label: d?.firstName + " " + d?.lastName,
          value: d?._id
        }));
        setEmployeesArray(makeOptions);
      }
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  useEffect(() => {
    if (isModalOpen) {
      getData();
    }
  }, [isModalOpen]);

  const items = (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      initialValues={{
        status: "Pending",
        paidStatus: false,
        leaveType: ""
      }}
    >
      <Row gutter={[16, 0]}>
        <Col xs={24} md={12}>
          <Form.Item
            name="employee"
            label={t("Employee")}
            rules={[{ required: true, message: t("Please select an employee") }]}
          >
            <Select
              className="w-100"
              options={employeesArray}
              placeholder={t("Select Employee")}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            name="leaveType"
            label={t("Leave Type")}
            rules={[{ required: true, message: t("Please select leave type") }]}
          >
            <Select
              className="w-100"
              placeholder={t("Select Leave Type")}
              options={leaveTypesArray}
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item name="status" label={t("Leave Status")}>
            <Select
              className="w-100"
              options={[
                { label: t("Pending"), value: "Pending" },
                { label: t("Approved"), value: "Approved" },
                { label: t("Rejected"), value: "Rejected" }
              ]}
              placeholder={t("Select Leave Status")}
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item name="paidStatus" label={t("Paid Status")}>
            <Select
              className="w-100"
              options={[
                { label: t("Paid"), value: true },
                { label: t("Unpaid"), value: false }
              ]}
              placeholder={t("Select Paid Status")}
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            name="startDate"
            label={t("Start Date")}
            rules={[
              { required: true, message: t("Please select start date") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const endDate = getFieldValue("endDate");
                  if (
                    !value ||
                    !endDate ||
                    value.isBefore(endDate) ||
                    value.isSame(endDate, "day")
                  ) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(t("Start date must be before or same as end date"))
                  );
                }
              })
            ]}
          >
            <DatePicker
              className="w-100"
              placeholder={t("Select Start Date")}
              format={"DD/MMM/YYYY"}
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            name="endDate"
            label={t("End Date")}
            rules={[
              { required: true, message: t("Please select end date") },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const startDate = getFieldValue("startDate");
                  if (
                    !value ||
                    !startDate ||
                    value.isAfter(startDate) ||
                    value.isSame(startDate, "day")
                  ) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(t("End date must be after or same as start date"))
                  );
                }
              })
            ]}
          >
            <DatePicker
              className="w-100"
              placeholder={t("Select End Date")}
              format={"DD/MMM/YYYY"}
            />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item
            name="reason"
            label={t("Reason")}
            rules={[{ required: true, message: t("Please enter reason") }]}
          >
            <TextArea placeholder={t("Enter Reason")} rows={4} className="w-100" />
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item name="note" label={t("Note")}>
            <TextArea placeholder={t("Enter Note")} rows={2} className="w-100" />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );

  return (
    <div className="AddLeaves">
      <MainButtonComp text="Add Leave" addBtn onClick={showModal} />
      <GeneralModal
        title={t("Add Leave")}
        onOk={() => form.submit()}
        open={isModalOpen}
        okText={t("Save")}
        confirmLoading={loading}
        onCancel={handleCancel}
        items={items}
        width={700}
      />
    </div>
  );
};

export default AddLeaves;
